// WHAT: Main application component for Tuya smart home device management
// WHY:  Provides comprehensive interface for managing Tuya devices with auto-refresh, rate limiting, and device control
// DOMAIN: SmartHome → Tuya
// DEPENDENCIES: tuyaService, TuyaLogo, Lucide icons, UI components
// RISKS:
//   - NOTE: Auto-refresh uses multiple intervals (backend + frontend) - may cause sync issues
//   - NOTE: Rate limiting calculations are complex - may not accurately reflect API limits
//   - NOTE: Device state management is complex - may have race conditions
//   - NOTE: Error handling is minimal - errors may be silently ignored

import {
  Lightbulb,
  Lock,
  Camera,
  Power,
  Settings,
  Plus,
  RefreshCw,
  Wifi,
  WifiOff,
  AlertCircle,
  Play,
  Square,
  ChevronDown,
  ChevronUp,
  Clock,
  Zap,
  Shield,
  Info,
} from "lucide-react";
import React, { useState, useEffect } from "react";

import { TuyaLogo } from "@/components/icons/workspaces";
import {
  tuyaService,
  type TuyaDevice,
  type AutoRefreshConfig,
  type AutoRefreshStatus,
} from "@/domains/smarthome/services";

/**
 * WHAT:
 *   Main application component for managing Tuya smart home devices with comprehensive
 *   features including device control, auto-refresh, rate limiting, and offline device handling.
 *
 * WHY:
 *   Provides a unified interface for managing all Tuya devices with advanced features
 *   like automatic status refresh and rate limit management to prevent API throttling.
 *
 * BEHAVIOR:
 *   - Auto-initializes Tuya service on mount
 *   - Loads devices and auto-refresh configuration
 *   - Manages auto-refresh with rate limiting
 *   - Provides device control (on/off, brightness, etc.)
 *   - Filters and displays online/offline devices
 *   - Updates rate limit info based on device count
 *   - Handles frontend refresh interval separately from backend
 *
 * RISKS:
 *   - NOTE: Auto-refresh uses multiple intervals (backend + frontend) - may cause sync issues
 *   - NOTE: Rate limiting calculations are complex - may not accurately reflect API limits
 *   - NOTE: Device state management is complex - may have race conditions
 *   - NOTE: Error handling is minimal - errors may be silently ignored
 *   - NOTE: StrictMode guard may prevent initialization in development
 *
 * @returns {JSX.Element} Tuya application component
 *
 */
// Reason: TuyaApp is used in src/routes/index.tsx but ESLint import/no-unused-modules doesn't detect it

export const TuyaApp: React.FC = () => {
  const [devices, setDevices] = useState<TuyaDevice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showOfflineDevices, setShowOfflineDevices] = useState(false);

  // Auto-Refresh State
  const [autoRefreshConfig, setAutoRefreshConfig] = useState<AutoRefreshConfig>({
    enabled: false,
    interval_seconds: 30,
    min_interval_seconds: 1,
    rate_limit_per_second: 50,
    recommended_interval_seconds: 2,
  });
  const [autoRefreshStatus, setAutoRefreshStatus] = useState<AutoRefreshStatus>({
    is_active: false,
    rate_limit_info: {
      current_interval_seconds: 30,
      is_valid: true,
      min_interval_seconds: 1,
      online_devices: 0,
      rate_limit_per_second: 50,
      recommended_interval_seconds: 2,
      warning_message: "",
    },
    total_refreshes: 0,
  });
  const [showAutoRefreshSettings, setShowAutoRefreshSettings] = useState(false);
  const [temporaryInterval, setTemporaryInterval] = useState(30);
  const [frontendRefreshInterval, setFrontendRefreshInterval] = useState<
    NodeJS.Timeout | undefined
  >();

  // Auto-initialize Tuya and load devices on mount
  useEffect(() => {
    let didRun = false;
    const initializeAndLoad = async () => {
      if (didRun) {
        return;
      } // StrictMode guard
      didRun = true;
      try {
        await tuyaService.autoInitialize();
        await loadDevices();
        await loadAutoRefreshData();
      } catch {
        // Failed to initialize Tuya
      }
    };
    void initializeAndLoad();
    return () => {
      didRun = true;
    };
    // Reason: Effect should only run once on mount for initialization, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Effect should only run once on mount
  }, []);

  // Update rate limit info when devices change
  useEffect(() => {
    void updateRateLimitInfo();
    // Reason: updateRateLimitInfo is a stable function that uses devices and temporaryInterval from closure
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateRateLimitInfo is stable and uses closure values
  }, [devices, temporaryInterval]);

  /**
   * WHAT:
   *   Loads auto-refresh configuration and status from service.
   *
   * WHY:
   *   Initializes auto-refresh state and starts frontend timer if backend refresh is active.
   *
   * SIDE EFFECTS:
   *   - Updates autoRefreshConfig state
   *   - Updates autoRefreshStatus state
   *   - Sets tempInterval from config
   *   - Starts frontend refresh interval if backend is active
   *
   * RISKS:
   *   - NOTE: Silent error handling - failures are not shown to user
   *   - NOTE: Frontend interval may not sync with backend interval
   */
  const loadAutoRefreshData = async () => {
    try {
      // NOTE: Load config and status in parallel for better performance
      const [config, status] = await Promise.all([
        tuyaService.getAutoRefreshConfig(),
        tuyaService.getAutoRefreshStatus(),
      ]);
      setAutoRefreshConfig(config);
      setAutoRefreshStatus(status);
      setTemporaryInterval(config.interval_seconds);

      // NOTE: Start frontend timer if auto-refresh is already active - syncs with backend
      if (status.is_active && !frontendRefreshInterval) {
        startFrontendAutoRefresh();
      }
    } catch {
      // NOTE: Failed to load auto-refresh data - silent failure
    }
  };

  /**
   * WHAT:
   *   Updates rate limit information based on online device count and interval.
   *
   * WHY:
   *   Provides rate limit warnings and recommendations based on current device count.
   *
   * SIDE EFFECTS:
   *   - Updates rate_limit_info in autoRefreshStatus state
   *
   * RISKS:
   *   - NOTE: Silent error handling - failures are not shown to user
   *   - NOTE: Rate limit calculations may not be accurate for all API configurations
   */
  const updateRateLimitInfo = async () => {
    try {
      // NOTE: Count only online devices - offline devices don't consume API quota
      const onlineDevices = devices.filter((d) => d.status === "Online").length;
      const rateLimitInfo = await tuyaService.getRateLimitInfo(onlineDevices, temporaryInterval);
      setAutoRefreshStatus((previous) => ({
        ...previous,
        rate_limit_info: rateLimitInfo,
      }));
    } catch {
      // NOTE: Failed to update rate limit info - silent failure
    }
  };

  const loadDevices = async () => {
    try {
      setIsLoading(true);
      setError(undefined);

      // Check if connected
      const connected = await tuyaService.isConnected();
      setIsConnected(connected);

      if (!connected) {
        setError("Tuya not connected. Please configure your credentials first.");
        return;
      }

      const deviceList = await tuyaService.getDevices();
      setDevices(deviceList);
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to load devices");
      // Failed to load devices
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * WHAT:
   *   Toggles device state (on/off) with optimistic UI update.
   *
   * WHY:
   *   Provides immediate UI feedback while API call is in progress.
   *
   * INPUTS:
   *   - deviceId: string — Device ID to toggle
   *
   * SIDE EFFECTS:
   *   - Updates device state optimistically
   *   - Sets error state on failure
   *
   * RISKS:
   *   - NOTE: Optimistic update may cause UI inconsistency if API call fails
   *   - NOTE: Device not found returns early - no error shown to user
   *
   * @param deviceId
   *
   */
  const toggleDevice = async (deviceId: string) => {
    try {
      // NOTE: Find device in current state - early return if not found
      const device = devices.find((d) => d.id === deviceId);
      if (!device) {
        return;
      }

      // NOTE: Toggle device state via service - returns success boolean
      const success = await tuyaService.toggleDevice(deviceId, !device.state);
      if (success) {
        // NOTE: Optimistic update - update UI immediately before API confirms
        setDevices((previous) =>
          previous.map((device) =>
            device.id === deviceId ? { ...device, state: !device.state } : device,
          ),
        );
      }
    } catch (error_) {
      // NOTE: Failed to toggle device - show error message to user
      setError(error_ instanceof Error ? error_.message : "Failed to toggle device");
    }
  };

  const refreshDevices = async () => {
    await loadDevices();
  };

  // Auto-Refresh Functions
  const startAutoRefresh = async () => {
    try {
      const success = await tuyaService.startAutoRefresh();
      if (success) {
        await loadAutoRefreshData();
        // Start frontend timer to update devices periodically
        startFrontendAutoRefresh();
      }
    } catch {
      // Failed to start auto-refresh
      setError("Failed to start auto-refresh");
    }
  };

  const stopAutoRefresh = async () => {
    try {
      const success = await tuyaService.stopAutoRefresh();
      if (success) {
        await loadAutoRefreshData();
        // Stop frontend timer
        stopFrontendAutoRefresh();
      }
    } catch {
      // Failed to stop auto-refresh
      setError("Failed to stop auto-refresh");
    }
  };

  const applyAutoRefreshSettings = async () => {
    try {
      const newConfig = {
        ...autoRefreshConfig,
        interval_seconds: temporaryInterval,
      };
      await tuyaService.updateAutoRefreshConfig(newConfig);
      setAutoRefreshConfig(newConfig);
      setShowAutoRefreshSettings(false);
    } catch {
      // Failed to apply auto-refresh settings
      setError("Failed to apply auto-refresh settings");
    }
  };

  const applyRecommendedSettings = async () => {
    const recommendedInterval = autoRefreshStatus.rate_limit_info.recommended_interval_seconds;
    setTemporaryInterval(recommendedInterval);
    const newConfig = {
      ...autoRefreshConfig,
      interval_seconds: recommendedInterval,
    };
    try {
      await tuyaService.updateAutoRefreshConfig(newConfig);
      setAutoRefreshConfig(newConfig);
      setShowAutoRefreshSettings(false);
    } catch {
      // Failed to apply recommended settings
      setError("Failed to apply recommended settings");
    }
  };

  // Frontend Auto-Refresh Functions
  /**
   * WHAT:
   *   Refreshes online devices and updates state.
   *
   * WHY:
   *   Extracted to reduce nesting depth and improve readability.
   *
   * @returns {Promise<void>} Promise that resolves when refresh completes
   *
   */
  const refreshDevicesAndUpdateState = async (): Promise<void> => {
    try {
      const updatedDevices = await tuyaService.refreshOnlineDevices();
      setDevices((previousDevices) => {
        // Merge updated devices with existing devices
        const updatedDeviceMap = new Map(updatedDevices.map((d) => [d.id, d]));
        return previousDevices.map((device) => {
          const updatedDevice = updatedDeviceMap.get(device.id);
          if (updatedDevice !== undefined) {
            return updatedDevice;
          }
          return device;
        });
      });
      await loadAutoRefreshData(); // Update auto-refresh status
    } catch {
      // Failed to refresh devices
    }
  };

  const startFrontendAutoRefresh = () => {
    stopFrontendAutoRefresh(); // Clear existing timer

    const interval = setInterval(() => {
      void refreshDevicesAndUpdateState();
    }, autoRefreshConfig.interval_seconds * 1000);

    setFrontendRefreshInterval(interval);
  };

  const stopFrontendAutoRefresh = () => {
    if (frontendRefreshInterval) {
      clearInterval(frontendRefreshInterval);
      setFrontendRefreshInterval(undefined);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopFrontendAutoRefresh();
    };
    // Reason: Effect should only run once on mount for cleanup, not on every render
    // stopFrontendAutoRefresh is stable and doesn't need to be in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Cleanup effect should only run once
  }, []);

  // Filter devices based on online status
  const onlineDevices = devices.filter((device) => device.status === "Online");
  const offlineDevices = devices.filter((device) => device.status === "Offline");
  const displayDevices = showOfflineDevices ? devices : onlineDevices;

  const getDeviceIcon = (type: TuyaDevice["device_type"]) => {
    switch (type) {
      case "Light": {
        return <Lightbulb className="w-6 h-6" />;
      }
      case "Switch": {
        return <Power className="w-6 h-6" />;
      }
      case "Sensor": {
        return <Power className="w-6 h-6" />;
      }
      case "Camera": {
        return <Camera className="w-6 h-6" />;
      }
      case "Lock": {
        return <Lock className="w-6 h-6" />;
      }
      case "Thermostat": {
        return <Power className="w-6 h-6" />;
      }
      case "Curtain": {
        return <Power className="w-6 h-6" />;
      }
      case "Fan": {
        return <Power className="w-6 h-6" />;
      }
      default: {
        return <Power className="w-6 h-6" />;
      }
    }
  };

  const getDeviceColor = (type: TuyaDevice["device_type"]) => {
    switch (type) {
      case "Light": {
        return "text-yellow-400";
      }
      case "Switch": {
        return "text-blue-400";
      }
      case "Sensor": {
        return "text-green-400";
      }
      case "Camera": {
        return "text-purple-400";
      }
      case "Lock": {
        return "text-red-400";
      }
      case "Thermostat": {
        return "text-orange-400";
      }
      case "Curtain": {
        return "text-indigo-400";
      }
      case "Fan": {
        return "text-cyan-400";
      }
      default: {
        return "text-gray-400";
      }
    }
  };

  const getStatusIcon = (status: TuyaDevice["status"]) => {
    switch (status) {
      case "Online": {
        return <Wifi className="w-4 h-4 text-green-500" />;
      }
      case "Offline": {
        return <WifiOff className="w-4 h-4 text-red-500" />;
      }
      case "Error": {
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      }
      default: {
        return <WifiOff className="w-4 h-4 text-gray-500" />;
      }
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
              <TuyaLogo size="app" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Tuya Smart Home</h1>
              <p className="text-neutral-400">
                {isConnected ? "Connected to Tuya Cloud" : "Not connected"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors disabled:opacity-50"
              disabled={isLoading}
              onClick={() => {
                void refreshDevices();
              }}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors">
              <Plus className="w-4 h-4" />
              Add Device
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        ) : null}

        {/* Connection Status */}
        {!isConnected && !error && (
          <div className="mb-6 p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              <p className="text-yellow-400">
                Tuya not connected. Please configure your credentials in Settings.
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-neutral-800 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Power className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{devices.length}</p>
                <p className="text-sm text-neutral-400">Total Devices</p>
              </div>
            </div>
          </div>
          <div className="bg-neutral-800 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {devices.filter((d) => d.device_type === "Light" && d.state).length}
                </p>
                <p className="text-sm text-neutral-400">Lights On</p>
              </div>
            </div>
          </div>

          <div className="bg-neutral-800 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {devices.filter((d) => d.status === "Online").length}
                </p>
                <p className="text-sm text-neutral-400">Online</p>
              </div>
            </div>
          </div>
        </div>

        {/* Auto-Refresh Section */}
        <div className="bg-neutral-800 rounded-xl p-6 mb-8 border border-neutral-700/50">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Auto-Refresh</h2>
                <p className="text-sm text-neutral-400">Automatische Geräteaktualisierung</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Status Indicator */}
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${autoRefreshStatus.is_active ? "bg-green-500" : "bg-gray-500"}`}
                />
                <span className="text-sm text-neutral-400">
                  {autoRefreshStatus.is_active ? "Aktiv" : "Inaktiv"}
                </span>
              </div>

              {/* Start/Stop Button */}
              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  autoRefreshStatus.is_active
                    ? "bg-red-500 hover:bg-red-600 text-white"
                    : "bg-green-500 hover:bg-green-600 text-white"
                }`}
                onClick={() => {
                  void (autoRefreshStatus.is_active ? stopAutoRefresh() : startAutoRefresh());
                }}
              >
                {autoRefreshStatus.is_active ? (
                  <>
                    <Square className="w-4 h-4" />
                    Stop
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Start
                  </>
                )}
              </button>

              {/* Settings Button */}
              <button
                className="flex items-center gap-2 px-4 py-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg transition-colors"
                onClick={() => {
                  setShowAutoRefreshSettings(!showAutoRefreshSettings);
                }}
              >
                <Settings className="w-4 h-4" />
                {showAutoRefreshSettings ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Settings Dropdown */}
          {showAutoRefreshSettings ? (
            <div className="space-y-6">
              {/* Rate Limit Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-neutral-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wifi className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-white">Online Geräte</span>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {autoRefreshStatus.rate_limit_info.online_devices}
                  </p>
                </div>

                <div className="bg-neutral-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-orange-400" />
                    <span className="text-sm font-medium text-white">Min. Intervall</span>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {autoRefreshStatus.rate_limit_info.min_interval_seconds}s
                  </p>
                </div>

                <div className="bg-neutral-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm font-medium text-white">Empfehlung</span>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {autoRefreshStatus.rate_limit_info.recommended_interval_seconds}s
                  </p>
                </div>

                <div className="bg-neutral-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-medium text-white">Properties API</span>
                  </div>
                  <p className="text-2xl font-bold text-white">50/s</p>
                </div>
              </div>

              {/* Additional Rate Limit Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-neutral-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <RefreshCw className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-medium text-white">Devices API</span>
                  </div>
                  <p className="text-2xl font-bold text-white">50/s</p>
                  <p className="text-xs text-neutral-400">Geräte auflisten</p>
                </div>

                <div className="bg-neutral-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Settings className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm font-medium text-white">Model API</span>
                  </div>
                  <p className="text-2xl font-bold text-white">10/s</p>
                  <p className="text-xs text-neutral-400">Gerätemodell</p>
                </div>

                <div className="bg-neutral-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Power className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-medium text-white">Control API</span>
                  </div>
                  <p className="text-2xl font-bold text-white">4/s</p>
                  <p className="text-xs text-neutral-400">Geräte steuern</p>
                </div>
              </div>

              {/* Live Validation Warning */}
              {!autoRefreshStatus.rate_limit_info.is_valid && (
                <div className="p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                    <p className="text-yellow-400 font-medium">Rate Limit Warnung</p>
                  </div>
                  <div className="text-yellow-400 text-sm whitespace-pre-line">
                    {autoRefreshStatus.rate_limit_info.warning_message}
                  </div>
                </div>
              )}

              {/* Rate Limit Explanation */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-blue-400">Rate Limit Erklärung</span>
                </div>
                <div className="text-sm text-neutral-300 space-y-1">
                  <p>
                    • <strong>Properties API (50/s):</strong> Für Auto-Refresh verwendet - 1 Call
                    pro Gerät
                  </p>
                  <p>
                    • <strong>Devices API (50/s):</strong> Für Auto-Refresh verwendet - 1 Call pro
                    Zyklus
                  </p>
                  <p>
                    • <strong>Model API (10/s):</strong> Für Gerätemodell-Abfragen
                  </p>
                  <p>
                    • <strong>Control API (4/s):</strong> Für Gerätesteuerung
                  </p>
                  <p className="text-xs text-neutral-400 mt-2">
                    Das empfohlene Intervall berücksichtigt alle API-Limits und fügt einen
                    Sicherheitspuffer hinzu.
                  </p>
                </div>
              </div>

              {/* Interval Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-white">Aktualisierungsintervall</label>
                  <span className="text-sm text-neutral-400">{temporaryInterval} Sekunden</span>
                </div>

                <div className="space-y-2">
                  <input
                    className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer slider"
                    max="120"
                    min={autoRefreshStatus.rate_limit_info.min_interval_seconds}
                    onChange={(e) => {
                      setTemporaryInterval(Number.parseInt(e.target.value));
                    }}
                    type="range"
                    value={temporaryInterval}
                  />
                  <div className="flex justify-between text-xs text-neutral-400">
                    <span>{autoRefreshStatus.rate_limit_info.min_interval_seconds}s</span>
                    <span>120s</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    className="w-20 px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-lg text-white text-sm"
                    max="120"
                    min={autoRefreshStatus.rate_limit_info.min_interval_seconds}
                    onChange={(e) => {
                      setTemporaryInterval(Number.parseInt(e.target.value) || 1);
                    }}
                    type="number"
                    value={temporaryInterval}
                  />
                  <span className="text-sm text-neutral-400">Sekunden</span>
                </div>
              </div>

              {/* Apply Buttons */}
              <div className="flex items-center gap-3">
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                  onClick={() => {
                    void applyAutoRefreshSettings();
                  }}
                >
                  <Settings className="w-4 h-4" />
                  Einstellungen übernehmen
                </button>
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg transition-colors"
                  onClick={() => {
                    void applyRecommendedSettings();
                  }}
                >
                  <Zap className="w-4 h-4" />
                  Empfohlen verwenden
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Device Management */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Geräte</h2>
              <p className="text-neutral-400 text-sm">
                Deine Tuya-Geräte werden automatisch geladen.
              </p>
            </div>
            {offlineDevices.length > 0 && (
              <button
                className="flex items-center gap-2 px-4 py-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg transition-colors text-sm"
                onClick={() => {
                  setShowOfflineDevices(!showOfflineDevices);
                }}
              >
                {showOfflineDevices
                  ? "Nur Online anzeigen"
                  : `${String(offlineDevices.length)} Offline anzeigen`}
              </button>
            )}
          </div>

          {isLoading ? <div className="text-sm text-neutral-400">Lade Geräte...</div> : null}

          {displayDevices.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-neutral-300">
                  {showOfflineDevices ? "Alle Geräte" : "Online Geräte"}
                </h3>
                <div className="text-sm text-neutral-400">
                  {String(onlineDevices.length)} online
                  {offlineDevices.length > 0 && ` • ${String(offlineDevices.length)} offline`}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Devices Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayDevices.map((device) => (
            <div
              className="bg-neutral-800 rounded-xl p-6 border border-neutral-700/50 hover:border-orange-500/50 transition-all duration-200"
              key={device.id}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 bg-neutral-700 rounded-lg flex items-center justify-center ${getDeviceColor(device.device_type)}`}
                  >
                    {getDeviceIcon(device.device_type)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{device.name}</h3>
                    <p className="text-sm text-neutral-400 capitalize">{device.device_type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(device.status)}
                  <span className="text-xs text-neutral-400 capitalize">{device.status}</span>
                </div>
              </div>

              <div className="space-y-3">
                {device.device_type !== "Sensor" && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-400">Status</span>
                    <button
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        device.state
                          ? "bg-green-500/20 text-green-400"
                          : "bg-neutral-700 text-neutral-300"
                      } ${device.status === "Offline" ? "opacity-50 cursor-not-allowed" : "hover:bg-opacity-30"}`}
                      disabled={device.status === "Offline"}
                      onClick={() => {
                        void toggleDevice(device.id);
                      }}
                    >
                      {device.state ? "On" : "Off"}
                    </button>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button className="flex-1 px-3 py-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg text-sm transition-colors">
                    Details
                  </button>
                  <button className="p-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg transition-colors">
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
